import OrganizationTable from "@/components/admin/orgs/table";
import { Input } from "@papier/ui/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@papier/ui/components/ui/select";
import { createFileRoute } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useDeferredValue, useMemo } from "react";
import { z } from "zod";

const orgsSearchSchema = z.object({
  name: z
    .string()
    .optional()
    .transform((val) => (val?.length === 0 ? undefined : val)),
  page: z.number().int().min(1).catch(1),
  plan: z.enum(["all", "free", "pro"]).catch("all"),
  sortBy: z.string().optional(),
  sortDir: z.enum(["asc", "desc"]).optional(),
});

export const Route = createFileRoute("/admin/organizations")({
  component: OrganizationsPage,
  validateSearch: orgsSearchSchema,
});

function OrganizationsPage() {
  const { useNavigate, useSearch, useRouteContext } = Route;
  const navigate = useNavigate();
  const { queryClient, trpc } = useRouteContext();
  const { name, page, sortBy, sortDir, plan } = useSearch();

  const limit = 10;
  const deferredName = useDeferredValue(name);

  const queryInput = useMemo(
    () => ({
      name: deferredName,
      plan: plan !== "all" ? plan : undefined,
      sortBy,
      sortDirection: sortDir,
      page,
      limit,
    }),
    [deferredName, page, limit, sortBy, sortDir, plan],
  );

  const onSuccess = () => {
    queryClient.invalidateQueries(trpc.authTrpc.getAdminOrganizations.queryFilter());
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <nav className="mb-2 flex w-full flex-wrap items-end justify-between gap-2">
        <div className="flex items-end gap-2">
          <div className="relative">
            <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
            <Input
              type="text"
              placeholder="Search..."
              className="pl-10"
              value={name || ""}
              onChange={(e) => {
                navigate({
                  search: (prev) => ({ ...prev, name: e.target.value, page: 1 }),
                  replace: true,
                  resetScroll: false,
                });
              }}
            />
          </div>
          <Select
            value={plan}
            onValueChange={(value) => {
              navigate({
                search: (prev) => ({
                  ...prev,
                  plan: value,
                  page: 1,
                }),
                replace: true,
                resetScroll: false,
              });
            }}
          >
            <SelectTrigger className="flex w-[150px] items-center gap-2">
              <span className="flex items-center gap-2 capitalize">
                {plan === "all" ? "All Plans" : plan}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Plans</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </nav>

      <OrganizationTable onSuccess={onSuccess} filters={queryInput} />
    </div>
  );
}
